import React from 'react';
import { cn, sortableHeaderProps } from '../../utils';

interface TableProps {
  children: React.ReactNode;
  className?: string;
}

export function Table({ children, className }: TableProps) {
  return (
    <div className={cn('overflow-hidden border border-secondary-200 dark:border-secondary-700 rounded-lg', className)}>
      <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
        {children}
      </table>
    </div>
  );
}

interface TableHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export function TableHeader({ children, className }: TableHeaderProps) {
  return (
    <thead className={cn('bg-secondary-50 dark:bg-secondary-900 dark:bg-secondary-800', className)}>
      {children}
    </thead>
  );
}

interface TableBodyProps {
  children: React.ReactNode;
  className?: string;
}

export function TableBody({ children, className }: TableBodyProps) {
  return (
    <tbody className={cn('bg-white dark:bg-secondary-800 dark:bg-secondary-900 divide-y divide-secondary-200 dark:divide-secondary-700', className)}>
      {children}
    </tbody>
  );
}

interface TableRowProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  'data-testid'?: string;
}

export function TableRow({ children, className, onClick, 'data-testid': dataTestId }: TableRowProps) {
  return (
    <tr 
      className={cn(
        onClick && 'cursor-pointer hover:bg-secondary-50 dark:hover:bg-secondary-700 dark:hover:bg-secondary-800',
        className
      )}
      onClick={onClick}
      data-testid={dataTestId}
    >
      {children}
    </tr>
  );
}

interface TableHeadProps {
  children: React.ReactNode;
  className?: string;
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: () => void;
}

export function TableHead({ children, className, sortable, sortDirection, onSort }: TableHeadProps) {
  const sortProps = sortable && onSort
    ? sortableHeaderProps(onSort, !!sortDirection, sortDirection)
    : undefined;

  return (
    <th 
      className={cn(
        'px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider',
        sortable && 'cursor-pointer hover:bg-secondary-100 dark:hover:bg-secondary-600 dark:hover:bg-secondary-700 select-none',
        className
      )}
      {...sortProps}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortable && (
          <span className="text-secondary-500 dark:text-secondary-400" aria-hidden="true">
            {sortDirection === 'asc' && '↑'}
            {sortDirection === 'desc' && '↓'}
            {!sortDirection && '↕'}
          </span>
        )}
      </div>
    </th>
  );
}

interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
  className?: string;
}

export function TableCell({ children, className, ...props }: TableCellProps) {
  return (
    <td className={cn('px-6 py-4 whitespace-nowrap text-sm text-secondary-900 dark:text-white dark:text-secondary-100', className)} {...props}>
      {children}
    </td>
  );
}
